import React, { useMemo } from "react";
import {
  makeStyles,
  Box,
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from "@material-ui/core";
import { StyledTableCell, StyledTableRow } from "components/table";
import { SkeletonTableRow } from "components/skeleton/skeleton.component";
import { TablePaginationActions } from "components/tablePagination";
import RefreshTable from "components/table/refreshTable";
import { getLang } from "app/feature/constants";
import moment from "moment";
import "moment/min/locales.min";
import { currencies } from "lib/constants/currency";

const useStyle = makeStyles((theme) => ({
  tableContainer: {
    border: "1px solid #F2F4F7",
    width: "100%",
    borderRadius: 8,
  },
  table: {
    tableLayout: "auto",
    whiteSpace: "nowrap",
    borderCollapse: "separate",
    width: "100%",
    [theme.breakpoints.down("xs")]: {
      minWidth: 0,
    },
    "& th,td": {
      fontSize: ".75rem",
      borderRight: "1px solid #F2F4F7",
      borderBottom: "1px solid #F2F4F7",
      padding: theme.spacing(1, 2, 1, 2),
    },
    "& th": {
      color: theme.palette.text.secondary,
      fontWeight: theme.typography.fontWeightBold,
    },
    "& th:last-child, td:last-child": {
      width: 37,
      borderRight: "0px",
    },
    "& tr:last-child > td": {
      borderBottom: "none",
    },
  },
  emptyContainer: {
    backgroundColor: theme.palette.background.paper,
    textAlign: "center",
  },
  button: {
    alignItems: "center",
    padding: 0,
    minWidth: 0,
    marginLeft: theme.spacing(1),
  },
  attributeText: {
    color: "#b2b2b2",
    fontSize: 12,
    whiteSpace: "break-spaces",
  },
  statusChip: {
    borderRadius: "16px",
    padding: "4px 16px",
    boxShadow: "0px 2px 4px 0px #00000045",
    color: "#ffffff",
  },
}));

export default function CreditBalanceReloadLogTableComponent({
  rows,
  isFetching,
  isError,
  handleReload,
  handleChangePage,
  handleChangeRowsPerPage,
  totalFiltered,
  paginationOptions,
  tableFilter,
  lang,
  language,
  disbursementInfo,
}) {
  const classes = useStyle();

  if (language === "ZH") {
    moment.locale("zh-cn");
  } else if (language === "TL") {
    moment.locale("tl-ph");
  } else {
    moment.locale(language);
  }

  const exp = useMemo(() => {
    if (disbursementInfo?.currency) {
      return (
        currencies.find((c) => c.alpha3 === disbursementInfo.currency)?.exp ?? 2
      );
    }
    return 2;
  }, [disbursementInfo]);

  return (
    <>
      {!isFetching && isError && <RefreshTable handleReload={handleReload} />}
      {rows.length > 0 || isFetching ? (
        <Box>
          <TableContainer className={classes.tableContainer}>
            <Table
              className={classes.table}
              size="small"
              aria-label="assign table"
              stickyHeader
            >
              <TableHead>
                <TableRow>
                  <StyledTableCell>
                    {getLang(lang, "label.AMOUNT")}{" "}
                    {disbursementInfo ? `(${disbursementInfo.currency})` : ""}
                  </StyledTableCell>
                  <StyledTableCell>
                    {getLang(lang, "label.USAGE")} (%)
                  </StyledTableCell>
                  <StyledTableCell>
                    {getLang(lang, "label.RELOAD_DATE")}
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isFetching
                  ? [...new Array(5)].map((v, index) => (
                      <SkeletonTableRow key={index} columnCount={3} />
                    ))
                  : rows.map((row, index) => {
                      return (
                        <StyledTableRow
                          key={index}
                          role="checkbox"
                          tabIndex={-1}
                        >
                          <StyledTableCell>
                            <Box display="flex" justifyContent="flex-end">
                              {row.amount.toLocaleString("en-US", {
                                minimumFractionDigits: exp,
                                maximumFractionDigits: exp,
                              })}
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box display="flex" justifyContent="flex-end">
                              {row.usage.toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box>
                              <Typography variant="caption" display="inline">
                                {row.reload_date}
                              </Typography>
                            </Box>
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            ActionsComponent={TablePaginationActions}
            rowsPerPageOptions={paginationOptions}
            component="div"
            count={totalFiltered}
            rowsPerPage={tableFilter.rowsPerPage}
            page={tableFilter.page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            SelectProps={{
              classes: classes.toolbar,
            }}
            labelRowsPerPage={getLang(lang, "label.ROWS_PER_PAGE")}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} ${getLang(
                lang,
                "label.OF"
              )} ${count.toLocaleString("en-US")}`
            }
          />
        </Box>
      ) : null}
      {!isFetching && !isError && rows.length === 0 && (
        <Box>
          <TableContainer className={classes.tableContainer}>
            <Table
              className={classes.table}
              size="small"
              aria-label="assign table"
              stickyHeader
            >
              <TableHead>
                <TableRow>
                  <StyledTableCell>
                    {getLang(lang, "label.RELOAD_DATE")}
                  </StyledTableCell>
                  <StyledTableCell>
                    {getLang(lang, "label.AMOUNT")}{" "}
                    {disbursementInfo ? `(${disbursementInfo.currency})` : ""}
                  </StyledTableCell>
                  <StyledTableCell>
                    {getLang(lang, "label.USAGE")}(%)
                  </StyledTableCell>
                </TableRow>
              </TableHead>
            </Table>
          </TableContainer>
          <Box py={9} className={classes.emptyContainer}>
            <Typography
              variant="body1"
              style={{ paddingBottom: "16px", fontWeight: "bold" }}
            >
              {getLang(lang, "paragraph.NO_CREDIT_BALANCE_RELOAD_LOG")}
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
}
